/**
 * "Build with Velo" for one Setup Hub section: enter BUILDING (server-side
 * atomic guard — a lost race means someone else built/is building) → run the
 * section's creator → persist the outcome. Audit-hardened (2026-07-08):
 *  - the plan is RE-FETCHED at build start so a Velo-adjusted proposal or a
 *    concurrent finish is never built from a stale cache;
 *  - a rejected BUILDING PUT (illegalTransition on a finished section) only
 *    refreshes — it must never demote BUILT/DONE_MANUALLY back to PROPOSED;
 *  - resultLinks are persisted even on the failure path and the outcome PUT
 *    retries once, so created object ids survive and a retry only re-creates
 *    the items that actually failed (idempotent via okKeysFromLinks).
 */
import { useCallback, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query/queryKeys";
import { invalidateOfferingLists } from "@/lib/slotting/invalidateSlotting";
import { invalidateWebsiteKeys } from "@/pages/event/website/components/utils/invalidateWebsiteKeys";
import { getSetupPlan, putSetupPlanSection } from "@/lib/api/clients/EventSetupPlanClient";
import { SetupPlan, SetupPlanSection, SetupResultLink, SetupSectionKey } from "@/types/SetupPlan";
import type { Event } from "@/types/Event";
import { RegistrationType } from "@/types/enums/RegistrationType";
import {
  parseEmailsProposal,
  parseOfferingProposal,
  parseWebsiteProposal,
  parseWorkflowsProposal,
} from "@/pages/dashboard/create-event/directions/proposals";
import {
  emailTemplateIdsFromLinks,
  mergeResultLinks,
  okKeysFromLinks,
  resultsToLinks,
} from "@/pages/dashboard/create-event/directions/creators/resultsToLinks";

export type BuildOutcome = "built" | "partial" | "failed" | "nothingToBuild" | "stale";

function sectionOf(plan: SetupPlan | null | undefined, key: SetupSectionKey): SetupPlanSection | undefined {
  return plan?.sections?.find((s) => s.key === key);
}

/**
 * Art. 50 KI-VO: the section's server-set `proposalAiTool` as the creators
 * send it — a non-empty string, else undefined (no tool, as before).
 */
export function proposalAiToolOf(section: SetupPlanSection | undefined): string | undefined {
  const tool = section?.proposalAiTool;
  return typeof tool === "string" && tool.trim().length > 0 ? tool : undefined;
}

function toIso(value: string | Date | undefined | null): string {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? "" : date.toISOString();
}

/** PUT with one retry — the outcome write carries the created object ids. */
async function putSectionResilient(
  eventId: string,
  key: SetupSectionKey,
  update: { status: SetupPlanSection["status"]; resultLinks?: SetupResultLink[] },
): Promise<boolean> {
  try {
    await putSetupPlanSection(eventId, key, update);
    return true;
  } catch {
    try {
      await putSetupPlanSection(eventId, key, update);
      return true;
    } catch {
      return false;
    }
  }
}

export function useBuildSection(eventId: string, event: Event | undefined) {
  const locale = useLocale();
  const tWebsite = useTranslations("websiteBuilder");
  const tWebsiteDefaults = useTranslations("websiteBuilderDefaults");
  const queryClient = useQueryClient();
  const [buildingKey, setBuildingKey] = useState<SetupSectionKey | null>(null);

  const build = useCallback(
    async (key: SetupSectionKey): Promise<BuildOutcome> => {
      if (buildingKey) return "nothingToBuild";
      setBuildingKey(key);
      try {
        // Fresh read: Velo may have adjusted the proposal seconds ago, or a
        // concurrent client may have finished this section (cache is 30s stale).
        const plan = await getSetupPlan(eventId).catch(() => null);
        const target = sectionOf(plan, key);
        if (!plan || !target) return "stale";
        if (target.status === "BUILT" || target.status === "DONE_MANUALLY" || target.status === "BUILDING") {
          return "stale";
        }

        const previousLinks = target.resultLinks;
        const skipKeys = okKeysFromLinks(previousLinks);
        // Art. 50 Abs. 2 KI-VO (plan A2/D6/J3): read FRESH from the re-fetched
        // section — Velo may have rewritten the proposal seconds ago. The tool
        // rides every object the build creates from it.
        const proposalAiTool = proposalAiToolOf(target);

        // Entering BUILDING is atomically guarded server-side; a 400 here
        // means someone else finished/started meanwhile — refresh, DON'T
        // touch the section's status.
        try {
          await putSetupPlanSection(eventId, key, { status: "BUILDING" });
        } catch {
          return "stale";
        }

        let fresh: SetupResultLink[] | null = null;
        try {
          // The creators are loaded at call time: createPackEmails drags the
          // full email-preset catalog (~MBs of inline HTML) into its module
          // graph, and this hook sits in the app shell via POnboarding — a
          // static import would put that catalog into every page's initial JS.
          if (key === "EMAILS") {
            const proposal = parseEmailsProposal(target.proposal);
            if (proposal) {
              const { createPackEmails } = await import(
                "@/pages/dashboard/create-event/directions/creators/createPackEmails"
              );
              fresh = resultsToLinks(
                await createPackEmails(eventId, proposal, locale, skipKeys, proposalAiTool),
              );
            }
          } else if (key === "WORKFLOWS") {
            const proposal = parseWorkflowsProposal(target.proposal);
            if (proposal) {
              const { createPackWorkflows } = await import(
                "@/pages/dashboard/create-event/directions/creators/createPackWorkflows"
              );
              const templateIds = emailTemplateIdsFromLinks(sectionOf(plan, "EMAILS")?.resultLinks);
              fresh = resultsToLinks(
                await createPackWorkflows(eventId, proposal, templateIds, skipKeys),
              );
            }
          } else if (key === "OFFERING") {
            const proposal = parseOfferingProposal(target.proposal);
            if (proposal) {
              const { createPackOffering } = await import(
                "@/pages/dashboard/create-event/directions/creators/createPackOffering"
              );
              fresh = resultsToLinks(
                await createPackOffering(
                  eventId,
                  proposal,
                  {
                    startIso: toIso(event?.start),
                    endIso: toIso(event?.end),
                    timezone: event?.timeZone ?? "Europe/Berlin",
                  },
                  proposalAiTool,
                ),
              );
            }
          } else if (key === "WEBSITE") {
            const proposal = parseWebsiteProposal(target.proposal);
            if (proposal) {
              const { createPackWebsite } = await import(
                "@/pages/dashboard/create-event/directions/creators/createPackWebsite"
              );
              fresh = resultsToLinks(
                await createPackWebsite(eventId, proposal, {
                  locale,
                  eventName: event?.name ?? "",
                  startIso: toIso(event?.start),
                  registrationType:
                    (event?.registrationType as RegistrationType | undefined) ??
                    RegistrationType.INVITATION,
                  tWebsite: (k: string) => tWebsite(k),
                  tWebsiteDefaults: (k: string) => tWebsiteDefaults(k),
                }),
              );
            }
          }
        } catch {
          // Creators collect per-item failures and never throw; this catch is
          // a belt-and-braces guard for unexpected errors mid-run. We entered
          // BUILDING ourselves, so PROPOSED recovery (with prior links) is safe.
          await putSectionResilient(eventId, key, {
            status: "PROPOSED",
            ...(previousLinks ? { resultLinks: previousLinks } : {}),
          });
          return "failed";
        }

        if (fresh === null) {
          // Unparseable/absent proposal (GOALS/GUESTS or a Velo-mangled shape)
          // — release the BUILDING claim we took, nothing was created.
          await putSectionResilient(eventId, key, {
            status: "PROPOSED",
            ...(previousLinks ? { resultLinks: previousLinks } : {}),
          });
          return "nothingToBuild";
        }

        const merged = mergeResultLinks(previousLinks, fresh);
        const anyOk = merged.some((link) => link.ok);
        const allOk = merged.length > 0 && merged.every((link) => link.ok);
        // Persist links on BOTH outcomes — losing created ids would make the
        // idempotent retry re-create every object (duplicate templates).
        await putSectionResilient(eventId, key, {
          status: anyOk ? "BUILT" : "PROPOSED",
          resultLinks: merged,
        });
        return allOk ? "built" : anyOk ? "partial" : "failed";
      } finally {
        setBuildingKey(null);
        void queryClient.invalidateQueries({ queryKey: qk.setupPlan.doc(eventId) });
        // The created objects land in OTHER pages' caches (templates list,
        // workflows, offerings index, website landing) — mark those stale so
        // visiting them within staleTime shows what was just built.
        if (key === "EMAILS") {
          void queryClient.invalidateQueries({ queryKey: ["msgMail", eventId] });
        } else if (key === "WORKFLOWS") {
          void queryClient.invalidateQueries({ queryKey: ["eventAutomationWorkflows", eventId] });
        } else if (key === "OFFERING") {
          invalidateOfferingLists(queryClient, eventId);
        } else if (key === "WEBSITE") {
          invalidateWebsiteKeys(queryClient, eventId);
        }
      }
    },
    [eventId, event, buildingKey, locale, tWebsite, tWebsiteDefaults, queryClient],
  );

  return { build, buildingKey };
}
